/**
 * Router
 *
 * Main js file for defining the routes used in the Cortex app
 *
 **/
import Vue from 'vue';
import Router from 'vue-router';

import store from '../store';

import Forbidden from '@/components/error/Forbidden.vue';
import Unauthorized from '@/components/error/Unauthorized.vue';

import Settings from '@/components/settings/Settings.vue';
import GeneralSettings from '@/components/settings/GeneralSettings.vue';
import UsersSettings from '@/components/settings/UsersSettings.vue';
import UsermanualSettings from '@/components/settings/UsermanualSettings.vue';

import ExportJobs from '@/components/settings/ExportJobs.vue';

import AddUser from '@/components/settings/AddUser.vue';

import EditUser from '@/components/settings/EditUser.vue';
import EditUserPassword from '@/components/settings/EditUserPassword.vue';
import EditUserProfile from '@/components/settings/EditUserProfile.vue';

import EditUserPermissions from '@/components/settings/EditUserPermissions.vue';
import LogInHistory from '@/components/settings/LogInHistory.vue';
import EnableIncompleteTours from '@/components/settings/EnableIncompleteTours.vue';

import Client from '@/components/client/Client.vue';
import AddRelease from '@/components/client/AddRelease.vue';
import EditRelease from '@/components/client/EditRelease.vue';
import EditReleaseVersion from '@/components/client/EditReleaseVersion.vue';
import ReleaseSettings from '@/components/client/ReleaseSettings.vue';
// import ReleaseSettingsForClient from '@/components/settings/ReleaseSettingsForClient.vue';
import ClientListTab from '@/components/client/ClientListTab.vue';
import ClientSwitchTab from '@/components/client/ClientSwitchTab.vue';
import NewClient from '@/components/client/NewClient.vue';
import EditClient from '@/components/client/EditClient.vue';
import LibraryTab from '@/components/client/LibraryTab.vue';
import AnalyticsTab from '@/components/client/AnalyticsTab.vue';
import AnalyticsAudience from '@/components/client/AnalyticsAudience.vue';
import AnalyticsTopic from '@/components/client/AnalyticsTopic.vue';
import ToursTab from '@/components/client/ToursTab.vue';
import ExportsTab from '@/components/client/ExportsTab.vue';
import ExportDetailsTab from '@/components/client/ExportDetailsTab.vue';

import Login from '@/components/user/Login.vue';
import ForgotPassword from '@/components/user/ForgotPassword.vue';
import PlayVideoPdf from '@/components/user/PlayVideoPdf.vue';
import ForgotSent from '@/components/user/ForgotSent.vue';
import ResetPassword from '@/components/user/ResetPassword.vue';

import TourEditor from '@/components/tour/TourEditor.vue';
import TopicRouteTabs from '@/components/tour/TopicRouteTabs.vue';
import TourSettings from '@/components/tour/TourSettings.vue';
import TourGeneralTab from '@/components/tour/TourGeneralTab.vue';
import TourMediaTab from '@/components/tour/TourMediaTab.vue';
import TopicsTab from '@/components/tour/TopicsTab.vue';
import RoutesTab from '@/components/tour/RoutesTab.vue';
import NewTour from '@/components/tour/newTour.vue';
import TourRoute from '@/components/tour/TourRoute.vue';
import TourFigure from '@/components/tour/TourFigure.vue';
import NewTourFigure from '@/components/tour/NewTourFigure.vue';
import RouteFigure from '@/components/tour/RouteFigure.vue';
import TopicFigure from '@/components/tour/TopicFigure.vue';
import TriggerFigure from '@/components/tour/TriggerFigure.vue';
import TopicTriggerNoteTabs from '@/components/tour/TopicTriggerNoteTabs.vue';
import TriggersTab from '@/components/tour/TriggersTab.vue';
import TriggerSettingsTab from '@/components/tour/TriggerSettingsTab.vue';
import TriggerNotesTab from '@/components/tour/TriggerNotesTab.vue';
import TriggerNotesAddTab from '@/components/tour/TriggerNotesAddTab.vue';

Vue.use(Router);

const router = new Router({
  routes: [
    {path: '/', redirect: '/login'},
    {
      path: '/client',
      name: 'client',
      component: Client,
      children: [
        {
          path: '/client/list',
          name: 'ClientListTab',
          component: ClientListTab
        },
        {
          path: '/client/exportDetails',
          name: 'exportDetailsTab',
          component: ExportDetailsTab
        },
        {
          path: '/client/tours',
          name: 'toursTab',
          component: ToursTab
        },
        {
          path: '/client/library',
          name: 'libraryTab',
          component: LibraryTab
        },
        {
          path: '/client/analytics',
          name: 'analyticsTab',
          component: AnalyticsTab,
          children: [
            {
              path: '/client/analytics/audience',
              name: 'analyticsAudience',
              component: AnalyticsAudience
            },
            {
              path: '/client/analytics/topic',
              name: 'analyticsTopic',
              component: AnalyticsTopic
            }
          ]
        },
        {
          path: '/client/exports',
          name: 'exportsTab',
          component: ExportsTab
        },
        {
          path: '/client/new',
          name: 'NewClient',
          component: NewClient
        }
      ]
    },
    {
      path: '/clients/switch',
      name: 'ClientSwitchTab',
      component: ClientSwitchTab
    },
    {
      path: '/client/edit',
      name: 'EditClient',
      component: EditClient
    },
    {
      path: '/login',
      name: 'Login',
      component: Login
    },
    {
      path: '/forgot',
      name: 'ForgotPassword',
      component: ForgotPassword
    },
    {
      path: '/usermanual/video',
      name: 'PlayVideoPdf',
      component: PlayVideoPdf
    },
    {
      path: '/usermanual/pdf',
      name: 'PlayVideoPdf',
      component: PlayVideoPdf,
      title: 'Search'
    },
    {
      path: '/usermanual/faq',
      name: 'PlayVideoPdf',
      component: PlayVideoPdf,
      title: 'Search'
    },
    {
      path: '/email/sent',
      name: 'ForgotSent',
      component: ForgotSent
    },
    {
      path: '/reset/:id',
      name: 'ResetPassword',
      component: ResetPassword,
      props: true
    },
    {
      path: '/settings',
      name: 'settings',
      component: Settings,
      children: [
        {
          path: '/settings/release',
          name: 'releaseSettings',
          component: ReleaseSettings
        },
        {
          path: '/settings/exportjobs',
          name: 'exportjobs',
          component: ExportJobs
        },
        {
          path: '/settings/release/add',
          name: 'addRelease',
          component: AddRelease
        },
        {
          path: '/settings/release/edit',
          name: 'editRelease',
          component: EditRelease
        },
        {
          path: '/settings/release/editVersion',
          name: 'editReleaseVersion',
          component: EditReleaseVersion
        },
        {
          path: '/settings/general',
          name: 'generalSettings',
          component: GeneralSettings
        },
        {
          path: '/settings/usermanual',
          name: 'usermanual',
          component: UsermanualSettings
        },
        {
          path: '/settings/lohinhistory',
          name: 'lohinhistory',
          component: LogInHistory
        },
        {
          path: '/settings/enableincompletetours',
          name: 'enableincompletetours',
          component: EnableIncompleteTours
        },
        {
          path: '/settings/users',
          name: 'usersSettings',
          component: UsersSettings
        },
        {
          path: '/settings/users/add',
          name: 'usersSettingsAdd',
          component: AddUser
        }
      ]
    },
    {
      path: '/tour',
      name: 'TourEditor',
      redirect: '/tour/topicRoutes',
      component: TourEditor,
      children: [
        {
          path: '/tour/topicRoutes',
          name: 'topicRoutes',
          redirect: '/tour/topicRoutes/topics',
          components: {
            header: TourFigure,
            body: TopicRouteTabs
          },
          children: [
            {
              path: '/tour/topicRoutes/routes',
              name: 'RoutesTab',
              component: RoutesTab
            },
            {
              path: '/tour/topicRoutes/topics',
              name: 'TopicsTab',
              component: TopicsTab
            }
          ]
        },
        {
          path: '/tour/settings',
          name: 'TourSettingsTab',
          component: TourSettings,
          children: [
            {
              path: '/tour/settings/general',
              name: 'TourGeneralTab',
              component: TourGeneralTab
            },
            {
              path: '/tour/settings/media',
              name: 'TourMediaTab',
              component: TourMediaTab
            }
          ]
        },
        {
          path: '/tour/new',
          name: 'NewTour',
          components: {
            header: NewTourFigure,
            body: NewTour
          }
        },
        {
          path: '/tour/route',
          name: 'Route',
          components: {
            header: RouteFigure,
            body: TourRoute
          }
        },
        {
          path: '/tour/topic',
          name: 'Topic',
          redirect: '/tour/topic/triggers',
          components: {
            header: TopicFigure,
            body: TopicTriggerNoteTabs
          },
          children: [
            {
              path: '/tour/topic/triggers',
              name: 'TopicTriggers',
              component: TriggersTab
            },
            {
              path: '/tour/topic/notes',
              name: 'TopicNotes',
              component: TriggerNotesTab
            }
          ]
        },
        {
          path: '/tour/trigger',
          name: 'Trigger',
          redirect: '/tour/trigger/settings',
          components: {
            header: TriggerFigure,
            body: TopicTriggerNoteTabs
          },
          children: [
            {
              path: '/tour/trigger/settings',
              name: 'TriggerSettings',
              component: TriggerSettingsTab
            },
            {
              path: '/tour/trigger/notes',
              name: 'TriggerNotes',
              component: TriggerNotesAddTab
            }
          ]
        }
      ]
    },
    {
      path: '/user',
      name: 'UserEdit',
      component: EditUser,
      redirect: '/user/profile',
      children: [
        {
          path: '/user/profile',
          name: 'UserProfile',
          component: EditUserProfile
        },
        {
          path: '/user/password',
          name: 'UserPassword',
          component: EditUserPassword
        },
        {
          path: '/user/permissions',
          name: 'UserPermissions',
          component: EditUserPermissions
        }
      ]
    },
    {
      path: '/403',
      name: 'ForbiddenError',
      component: Forbidden
    },
    {
      path: '/401',
      name: 'UnauthorizedError',
      component: Unauthorized
    }
  ]
});

router.beforeEach((to, from, next) => {
  if (!store.state.user.isSignedIn) {
    if (to.path === '/login' || to.path === '/forgot' || to.path === '/email/sent' || to.path.startsWith('/reset/') || to.path === '/usermanual/video' || to.path === '/usermanual/pdf') {
      return next();
    }
    return next({path: '/login'});
  } else if (to.path !== '/403') {
    if (!store.state.user.isAdmin) {
      let userClientId = store.state.user.user.clientId;
      if (store.state.client.currentClient) {
        let currentClientId = store.state.client.currentClient.id;
        if (userClientId !== currentClientId) {
          store.dispatch('client/resetClient');
          return next({path: '/403'});
        } else if (store.state.tours.currentTour.id) {
          let currentTourClientId = store.state.tours.currentTour.clientId;
          if (userClientId !== currentTourClientId || currentClientId !== currentTourClientId) {
            store.dispatch('client/resetClient');
            return next({path: '/403'});
          }
        }
      }
    }
  }

  if (from.name === '401') {
    return next({path: '/login'});
  }

  if (from.name === '403' && to.name === 'client') {
    return next({path: '/login'});
  }

  if (to.name === 'generalSettings' || to.name === 'ClientListTab') {
    if (!store.getters['user/isAdmin']) {
      return next(false);
    }
  }

  if (from.name === 'Route') {
    store.dispatch('map/togglePencilIconSelected', false);
  }

  if (to.name !== 'TopicTriggers' && to.name !== 'TopicNotes' && to.name !== 'TriggerSettings' && to.name !== 'TriggerNotes') { // Might to adjust how this logic works or expand it's range of 'from' routes
    store.dispatch('topics/resetCurrentTopic');
  }

  if (from.name === 'TopicsTab') {
    store.dispatch('topics/resetSelectedTopics');
  }

  if (store.getters['editor/showSettings']) {
    store.dispatch('editor/toggleShowSettings', false);
  }

  if (to.matched[0].name === 'client') {
    if (store.state.tours.currentTour.id) {
      store.dispatch('library/clearTourNotifications');
      store.dispatch('tours/clearCurrentTour');
      store.dispatch('routes/clearCurrentRoute');
      store.dispatch('topics/resetCurrentTopic');
      store.dispatch('triggers/clearCurrentTrigger');
    }
  }

  if (to.name === 'ClientListTab') {
    if (store.state.client.currentClient) {
      store.dispatch('client/resetClient');
    }
  }

  if (store.state.routes.currentRoute.id) {
    if (to.name === 'TopicsTab' || to.name === 'RoutesTab') {
      store.dispatch('routes/clearCurrentRoute');
    }
  }

  store.dispatch('topics/resetTopicHover');
  store.dispatch('triggers/resetTriggerHover');

  next();
});

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js